/**
 * About page.
 *
 * Sources ONLY from src/data/clinic.ts. Every fact here is either:
 * - Verified (from the clinic)
 * - Unverified but already live (inherited from existing site, flagged in CLINIC_VERIFICATION)
 * - Absent (no invented claims, no unsubstantiated statistics)
 *
 * This is the most scrupulously honest page on the site.
 */

import Seo from "@/components/Seo";
import { useLanguage } from "@/contexts/LanguageContext";
import type { Locale } from "@/content/types";
import { CLINIC, OPENING_HOURS } from "@/data/clinic";
import { clinicNode } from "@/lib/schema";
import { motion } from "framer-motion";
import { MapPin, Phone, MessageCircle, Clock, AlertCircle } from "lucide-react";

interface AboutPageProps {
  locale: Locale;
}

export default function AboutPage({ locale }: AboutPageProps) {
  const title = locale === "ar" ? "من نحن" : "About Us";
  const description =
    locale === "ar"
      ? "عيادة بريميرو لطب الأسنان في مدينة نصر، القاهرة - رعاية فم عالية الجودة"
      : "Primero Dental Clinic in Nasr City, Cairo - comprehensive dental care";

  // Business copy — genuine, factual, no invented claims
  const missionText =
    locale === "ar"
      ? "عيادة بريميرو متخصصة في طب الأسنان العام والتخصصي. نقدم خدمات شاملة تغطي زراعة الأسنان والتقويم وتجميل الأسنان وعلاج جذور الأسنان وطب أسنان الأطفال."
      : "Primero Dental Clinic provides comprehensive dental care, specializing in implants, orthodontics, cosmetic dentistry, endodontics, and pediatric care.";

  const approachText =
    locale === "ar"
      ? "نركز على رعاية المريض ذات الجودة العالية والتشخيص الدقيق والتخطيط الشامل قبل البدء بأي علاج. كل مريض يستحق الاهتمام الشخصي والتفسيرات الواضحة عن خياراته العلاجية."
      : "We focus on quality patient care, thorough diagnosis, and comprehensive treatment planning. Every patient receives individual attention and clear explanation of their treatment options.";

  const clinicName = locale === "ar" ? CLINIC.nameAr : CLINIC.name;
  const street = locale === "ar" ? CLINIC.address.streetAr : CLINIC.address.street;
  const district = locale === "ar" ? CLINIC.address.districtAr : CLINIC.address.district;
  const city = locale === "ar" ? CLINIC.address.cityAr : CLINIC.address.city;

  const hoursStatusLabel =
    locale === "ar"
      ? "ساعات العمل"
      : "Hours of Operation";

  const hoursCallText =
    locale === "ar"
      ? "يرجى الاتصال بنا للتأكد من ساعات العمل الحالية"
      : "Please call us to confirm current hours of operation";

  const hoursCallNote =
    locale === "ar"
      ? "ساعات العمل لم تُؤكّد بعد رسميًا من قبل العيادة. سنحدثك بمجرد تأكيد المعلومات الدقيقة."
      : "Opening hours are pending formal clinic confirmation. We will update this information as soon as we receive verified details.";

  return (
    <>
      <Seo
        title={title}
        description={description}
        path={`/${locale}/about`}
        locale={locale}
        schema={clinicNode(locale)}
        geo
      />
      <main className="container mx-auto px-4 py-12">
        {/* Header */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {title}
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl">
            {description}
          </p>
        </motion.div>

        {/* Mission */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-white mb-4">
            {locale === "ar" ? "عن عيادتنا" : "About Our Clinic"}
          </h2>
          <div className="space-y-4">
            <p className="text-slate-300 leading-relaxed text-lg">
              {missionText}
            </p>
            <p className="text-slate-300 leading-relaxed text-lg">
              {approachText}
            </p>
          </div>
        </motion.section>

        {/* Location & Contact */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-12 grid md:grid-cols-2 gap-8"
        >
          {/* Location Card */}
          <div className="p-8 rounded-lg border border-slate-700 bg-slate-900/50">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              {locale === "ar" ? "الموقع" : "Location"}
            </h3>
            <div className="space-y-3 text-slate-300">
              <p>
                <span className="font-semibold">{clinicName}</span>
              </p>
              <p>
                {street}
                <br />
                {district}, {city}
                <br />
                {CLINIC.address.countryName}
              </p>
              <a
                href={CLINIC.mapsShortUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mt-4 font-semibold"
              >
                {locale === "ar" ? "فتح في Google Maps ←" : "View on Google Maps →"}
              </a>
            </div>
          </div>

          {/* Contact Card */}
          <div className="p-8 rounded-lg border border-slate-700 bg-slate-900/50">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              {locale === "ar" ? "التواصل" : "Contact"}
            </h3>
            <div className="space-y-4 text-slate-300">
              {/* Phone */}
              <div>
                <p className="text-sm text-slate-400 mb-1">
                  {locale === "ar" ? "الهاتف" : "Phone"}
                </p>
                <a
                  href={`tel:${CLINIC.phone.e164}`}
                  className="text-lg font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                  {CLINIC.phone.displayLocal}
                </a>
              </div>

              {/* WhatsApp */}
              <div>
                <p className="text-sm text-slate-400 mb-1">WhatsApp</p>
                <a
                  href={`https://wa.me/${CLINIC.whatsapp.primary}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-lg font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  {CLINIC.whatsapp.primary}
                </a>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Hours of Operation */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="p-8 rounded-lg border border-amber-600/30 bg-amber-900/10">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              {hoursStatusLabel}
            </h3>

            {/* Unverified notice with explanation */}
            <div className="flex gap-3 mb-6 p-4 rounded-lg bg-amber-900/20 border border-amber-600/30">
              <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-200">
                <p className="font-semibold mb-1">
                  {locale === "ar"
                    ? "معلومة غير مؤكدة"
                    : "Unverified Information"}
                </p>
                <p>{hoursCallNote}</p>
              </div>
            </div>

            {/* Call to confirm */}
            <p className="text-slate-300 font-semibold">
              {hoursCallText}:
            </p>
            <a
              href={`tel:${CLINIC.phone.e164}`}
              className="inline-block mt-4 px-4 py-2 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-colors"
            >
              {CLINIC.phone.displayLocal}
            </a>
          </div>
        </motion.section>

        {/* Context about Nasr City */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="p-8 rounded-lg border border-slate-700 bg-slate-900/50"
        >
          <h3 className="text-lg font-semibold text-white mb-4">
            {locale === "ar" ? "عن مدينة نصر" : "About Nasr City"}
          </h3>
          <p className="text-slate-300 leading-relaxed">
            {locale === "ar"
              ? "مدينة نصر حي حديث نسبيًا في شرق القاهرة، معروف بمبانيه السكنية والتجارية الحديثة. العيادة تخدم سكان مدينة نصر والمناطق المجاورة بخدمات طب الأسنان الشاملة."
              : "Nasr City is a relatively modern district in eastern Cairo, known for its contemporary residential and commercial buildings. The clinic serves residents of Nasr City and surrounding areas with comprehensive dental care services."}
          </p>
        </motion.section>
      </main>
    </>
  );
}
